import { useEnumStore } from '../store/enumStore';
import { getSkillLabel, getInterestLabel, getEventTypeLabel } from '../utils/enumHelpers';
import { useTranslation } from '../i18n';

/**
 * Custom hook that provides functions to get labels for skills, interests, and event types
 * Handles all the different field name variations from the API
 */
export const useEnumLabels = () => {
  const getSkillById = useEnumStore((state) => state.getSkillById);
  const getInterestById = useEnumStore((state) => state.getInterestById);
  // The getters read the store directly, so subscribe to the data as well to
  // re-render callers once the lookups arrive.
  useEnumStore((state) => state.skills);
  useEnumStore((state) => state.interests);
  const eventTypes = useEnumStore((state) => state.eventTypes);
  const { t } = useTranslation();

  /**
   * Get skill label by ID
   * @param id - Skill ID (number or string)
   * @returns Skill label or fallback string
   */
  const getSkillLabelById = (id: number | string): string => {
    const skillData = getSkillById(id);
    return getSkillLabel(skillData, t('common.unknownSkill', { id }));
  };

  /**
   * Get interest label by ID
   * @param id - Interest ID (number or string)
   * @returns Interest label or fallback string
   */
  const getInterestLabelById = (id: number | string): string => {
    const interestData = getInterestById(id);
    return getInterestLabel(interestData, t('common.unknownInterest', { id }));
  };

  /**
   * Get event type label by ID or type string
   * @param idOrType - Event type ID or type string
   * @returns Event type label or null
   */
  const getEventTypeLabelById = (idOrType: number | string): string | null => {
    const eventTypeData = eventTypes?.find((item) => 
      item.event_type === idOrType || 
      item.id === idOrType ||
      String(item.id) === String(idOrType)
    );
    return eventTypeData ? getEventTypeLabel(eventTypeData) : null;
  };

  return {
    getSkillLabelById,
    getInterestLabelById,
    getEventTypeLabelById,
  };
};
